// =============================================================================
// The mini-cart drawer.
//
// Opened by the header's cart button, closed by its ✕, the scrim, or Escape —
// the same contract the mobile nav keeps, and it shares overlay.js so the two
// drawers' scroll locks and Escape handlers cooperate instead of fighting (see
// overlay.js: whichever closes first must not unlock the page under the other).
//
// The drawer's interior is server-rendered and published as a WooCommerce cart
// fragment (inc/cart.php). Nothing here builds cart markup. A quantity change or
// a removal posts to the endpoint carried on the drawer and swaps the returned
// fragments in; a Store API add-to-cart — which bypasses WooCommerce's own
// fragments — broadcasts `aug:cart-updated`, which marks the drawer stale so it
// re-pulls the same endpoint (with no key: a bare refresh) the next time it
// opens. One renderer feeds the server output, the fragment and the AJAX
// response alike, so the three can never disagree about the markup.
// =============================================================================

import { lockScroll, releaseScroll, closeOnEscape } from "./overlay.js";

export default function initMiniCart() {
	const drawer = document.querySelector("[data-cart-drawer]");
	const openBtn = document.querySelector("[data-cart-open]");

	// No drawer (the cart and checkout pages render a plain link instead) or no
	// button to open it: nothing to do.
	if (!drawer || !openBtn) {
		return;
	}

	const scrim = document.querySelector("[data-cart-scrim]");

	let open = false;
	// Set when the cart changes through a path that does not re-render the
	// drawer (a Store API add-to-cart); cleared once the drawer has re-pulled.
	let stale = false;

	function setOpen(next) {
		// Guarding on the current state keeps this safe to call from Escape,
		// which closes every overlay whether or not it was open, so an
		// already-closed drawer never releases a lock belonging to another.
		if (next === open) {
			return;
		}
		open = next;

		drawer.classList.toggle("is-open", open);
		drawer.setAttribute("aria-hidden", open ? "false" : "true");
		openBtn.setAttribute("aria-expanded", open ? "true" : "false");
		if (scrim) {
			scrim.classList.toggle("is-open", open);
		}

		if (open) {
			lockScroll();
			// Refresh lazily, on open, rather than on every add: a visitor who
			// adds three things and never opens the drawer pays for no requests.
			if (stale) {
				stale = false;
				postCart("", 0, null);
			}
			// Focus follows the drawer so tabbing starts inside it, and returns
			// to the button that opened it on the way out.
			drawer.querySelector("[data-cart-close]")?.focus();
		} else {
			releaseScroll();
			openBtn.focus();
		}
	}

	openBtn.addEventListener("click", () => setOpen(!open));
	if (scrim) {
		scrim.addEventListener("click", () => setOpen(false));
	}
	closeOnEscape(() => setOpen(false));

	// Delegated on the drawer, not bound per control: the body is replaced
	// wholesale on every refresh, and listeners bound to the discarded nodes
	// would go with them.
	drawer.addEventListener("click", (event) => {
		if (event.target.closest("[data-cart-close]")) {
			setOpen(false);
			return;
		}

		const qtyBtn = event.target.closest("[data-cart-qty]");
		const removeBtn = event.target.closest("[data-cart-remove]");
		const btn = qtyBtn || removeBtn;

		if (!btn) {
			return;
		}

		const item = btn.closest("[data-cart-key]");
		if (!item) {
			return;
		}

		// A remove posts 0, which drops the line server-side; a stepper adds its
		// ±1 to the quantity currently shown.
		let quantity = 0;
		if (qtyBtn) {
			const value = item.querySelector("[data-cart-qty-value]");
			if (!value) {
				return;
			}
			quantity = parseInt(value.textContent, 10) + parseInt(qtyBtn.dataset.cartQty, 10);
			if (!Number.isFinite(quantity) || quantity < 0) {
				return;
			}
		}

		postCart(item.dataset.cartKey, quantity, btn);
	});

	// The Store API add-to-cart path updates the badge itself (cart-count.js)
	// but cannot re-render this server-side drawer, so it announces the change
	// and the drawer catches up the next time it is opened.
	document.addEventListener("aug:cart-updated", () => {
		stale = true;
	});

	/**
	 * Post a quantity change — or, with an empty key, a bare refresh — and swap
	 * the returned badge/body fragments into the page.
	 *
	 * @param {string} key Cart line key; empty to only re-read the cart.
	 * @param {number} quantity New quantity; 0 removes the line.
	 * @param {HTMLElement|null} busyEl Control to disable while in flight.
	 */
	function postCart(key, quantity, busyEl) {
		const endpoint = drawer.dataset.cartAjax;
		if (!endpoint) {
			return;
		}

		const body = new URLSearchParams({ nonce: drawer.dataset.cartNonce || "" });
		if (key) {
			body.set("cart_item_key", key);
			body.set("quantity", String(quantity));
		}

		if (busyEl) {
			busyEl.disabled = true;
		}

		fetch(endpoint, { method: "POST", body, credentials: "same-origin" })
			.then((res) => res.json())
			.then((json) => {
				const fragments = json?.data?.fragments;
				if (!fragments) {
					return;
				}
				Object.entries(fragments).forEach(([selector, html]) => {
					// outerHTML, not replaceWith: the fragment is a markup string
					// that has to be parsed, and the replacement carries the same
					// selector, so the next swap still finds its target.
					document.querySelectorAll(selector).forEach((el) => {
						el.outerHTML = html;
					});
				});
			})
			.catch(() => {
				// A failed refresh leaves the last-good drawer in place; the next
				// open marks it stale again and retries.
				stale = true;
			})
			.finally(() => {
				if (busyEl) {
					busyEl.disabled = false;
				}
			});
	}
}
